'use client';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import cn from 'classnames';
import { useUserDeviceType, DeviceTypes } from '../../../hooks';
import styles from './appDownloadButtons.module.scss';
import { Link, type LinkProps } from '@/ui-components/atoms/Link/Link';

const ARIA_LABEL = {
    appleStore: 'Get the SXM iOS app from the App Store',
    playStore: 'Get the SXM Android app from the Play Store',
};

export type DownloadVariant = 'appleStore' | 'playStore';

export interface DownloadButtonProps {
    storeLink: LinkProps;
    badgeSrc: string;
    type: DownloadVariant;
    className?: string;
}

const DownloadButton = ({ storeLink, type, badgeSrc, className }: DownloadButtonProps) => {
    const badgeClasses = cn(styles.appStoreBadge, className);
    const data = {
        'data-linkname': type,
    };

    return (
        <Link className={badgeClasses} {...storeLink} {...data} ref={null}>
            {/* image size optimised based on container */}
            <ImageWrapper
                src={badgeSrc}
                alt={ARIA_LABEL[type]}
                fetchPriority="low"
                optimizedWidth={175}
                optimizedHeight={55}
            />
        </Link>
    );
};

interface AppDownloadButtonProps {
    items: DownloadButtonProps[];
    className?: string;
    androidClassName?: string;
    appleClassName?: string;
}

export function AppDownloadButtons({
    items,
    className,
    androidClassName,
    appleClassName,
}: AppDownloadButtonProps): React.JSX.Element {
    const userDeviceType = useUserDeviceType();

    let appleBadge = null;
    let androidBadge = null;
    items.forEach((item) => {
        if (item.type === 'appleStore' && item.badgeSrc) {
            appleBadge = (
                <DownloadButton
                    badgeSrc={item.badgeSrc}
                    type={item.type}
                    storeLink={item.storeLink}
                    className={appleClassName}
                />
            );
        } else if (item.type === 'playStore' && item.badgeSrc) {
            androidBadge = (
                <DownloadButton
                    badgeSrc={item.badgeSrc}
                    type={item.type}
                    storeLink={item.storeLink}
                    className={androidClassName}
                />
            );
        }
    });

    const mainWrapperStyles = cn(styles.mainWrapper, className);

    if (!userDeviceType || !(appleBadge || androidBadge)) {
        return <></>;
    }

    return (
        <div className={mainWrapperStyles}>
            {userDeviceType === DeviceTypes.ios && appleBadge}
            {userDeviceType === DeviceTypes.android && androidBadge}
            {[DeviceTypes.desktop, DeviceTypes.unknown].includes(userDeviceType) && (
                <div className={styles.badgesWrapper}>
                    {appleBadge}
                    {androidBadge}
                </div>
            )}
        </div>
    );
}
